import { useCallback } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate, useParams } from "@tanstack/react-router"
import {
	IconClockOutline18 as ClockIcon,
	IconGlobe2Outline18 as GlobeIcon,
	IconCalendarOutline18 as JoinedIcon,
} from "nucleo-ui-outline-18"
import { useToggle } from "react-use"

import { MembershipRole } from "@productlane/db/browser"
import {
	AlertDialog,
	Badge,
	Button,
	CopyButton,
	Empty,
	IconButton,
} from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { EllipsisVerticalIcon as MoreVertical } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { queries } from "@productlane/zero/queries"

import EmptyCompany from "@/assets/empty-states/empty-company.png"
import EmptyIssue from "@/assets/empty-states/empty-issue.png"
import {
	DetailScreenEditor,
	DetailScreenLayout,
} from "@/components/detail-screen"
import {
	DetailThreadsList,
	useDetailThreadsControls,
} from "@/components/detail-screen/detail-threads-list"
import { useDetailListState } from "@/components/detail-screen/use-detail-list-state"
import {
	MemberFactRow,
	OutOfOfficeBadge,
	OutOfOfficeFact,
} from "@/components/member-hover-card"
import { MetaInfo } from "@/components/meta-info"
import { PresenceAvatar } from "@/components/presence-avatar"
import { toast } from "@/components/toast"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useBackNavigation, useSetBackNavigation } from "@/lib/back-navigation"
import { formatPresenceLabel } from "@/lib/format-last-seen"
import { formatLocalTime } from "@/lib/format-local-time"
import { isQuerySettled } from "@/lib/query-settled"
import { useSession } from "@/lib/session"
import { useIsMemberOnline } from "@/lib/states/presence-state"
import { ChangeRoleDialog } from "@/screens/settings/change-role-dialog"
import { roleChangeErrorMessage } from "@/screens/settings/member-roles"
import { DetailBreadcrumb, DetailTopBar } from "../page-header"

const ROLE_LABEL: Record<MembershipRole, string> = {
	ADMIN: "Admin",
	USER: "Member",
	VIEWER: "Viewer",
}

export const MembersDetailScreen = () => {
	const navigate = useNavigate()
	const params = useParams({ strict: false })
	const id = params.id!
	useSetBackNavigation({
		type: "members-detail",
		url: `/members/${id}`,
	})

	const { session } = useSession()
	const [membership, membershipResult] = useQuery(
		queries.membershipByUserId({ userId: id }),
	)
	const { data: lastSeenByUser } = api.workspace.memberLastSeen.useQuery(
		undefined,
		{ refetchInterval: 60_000 },
	)

	const isOnline = useIsMemberOnline(id)
	const threadsState = useDetailListState()
	const threadControls = useDetailThreadsControls()
	const [isRoleDialogOpen, setRoleDialogOpen] = useToggle(false)
	const [isConfirmRemoveOpen, setConfirmRemove] = useToggle(false)

	const context = api.useUtils()
	const kickMemberMutation = api.workspace.kickMember.useMutation()
	const makeAdminMutation = api.workspace.makeAdmin.useMutation()
	const makeMemberMutation = api.workspace.makeMember.useMutation()
	const makeViewerMutation = api.workspace.makeViewer.useMutation()

	const { goBack } = useBackNavigation()
	const close = useCallback(() => {
		goBack("/settings/members")
	}, [goBack])

	useShortcut("esc", close, { enabled: !isConfirmRemoveOpen })

	const changeRole = useCallback(
		async (role: MembershipRole) => {
			if (!membership) return
			analytics.track("change-member-role", {
				targetUserId: id,
				role,
				origin: "member-details-page",
			})
			try {
				if (role === MembershipRole.ADMIN) {
					await makeAdminMutation.mutateAsync({ membershipId: membership.id })
				} else if (role === MembershipRole.USER) {
					await makeMemberMutation.mutateAsync({ membershipId: membership.id })
				} else {
					await makeViewerMutation.mutateAsync({ membershipId: membership.id })
				}
				await context.workspace.members.invalidate()
				void context.workspace.trialInfo.invalidate()
				const memberName = membership.user?.name || membership.user?.email
				toast.success({
					title: `${memberName ?? "This member"} is now ${ROLE_LABEL[role]}`,
				})
			} catch (error) {
				toast.error({
					title: "The role didn't change",
					description: roleChangeErrorMessage(error),
				})
			}
		},
		[
			context.workspace.members,
			context.workspace.trialInfo,
			id,
			makeAdminMutation,
			makeMemberMutation,
			makeViewerMutation,
			membership,
		],
	)

	if (!membership?.user) {
		if (!isQuerySettled(membershipResult)) return null
		return <EmptyMemberDetailsScreen />
	}

	const user = membership.user
	const name = user.name || user.email
	const role = membership.role as MembershipRole
	const isAdmin = session?.membershipRole === MembershipRole.ADMIN
	const isSelf = session?.userId === id
	const lastSeen = lastSeenByUser?.[id]
	const lastSeenAt = lastSeen?.lastSeenAt
	const localTime = formatLocalTime(lastSeen?.timeZone)

	return (
		<>
			<MetaInfo title="Member Detail" />
			<AlertDialog
				isOpen={isConfirmRemoveOpen}
				onOpenChange={setConfirmRemove}
				confirmText="Remove"
				title={`Remove ${name} from this workspace?`}
				description="This person will no longer have access to this workspace."
				onCancel={() => setConfirmRemove(false)}
				onConfirm={async () => {
					analytics.track("kick-member", {
						targetUserId: id,
						origin: "member-details-page",
					})
					await kickMemberMutation.mutateAsync({ membershipId: membership.id })
					await context.workspace.members.invalidate()
					void context.workspace.trialInfo.invalidate()
					void navigate({ to: "/settings/members", replace: true })
				}}
			/>
			<ChangeRoleDialog
				isOpen={isRoleDialogOpen}
				onOpenChange={setRoleDialogOpen}
				memberName={name}
				currentRole={role}
				onSave={changeRole}
				isLoading={
					makeAdminMutation.isLoading ||
					makeMemberMutation.isLoading ||
					makeViewerMutation.isLoading
				}
			/>
			<DetailTopBar className="justify-between" clearsNavToggle>
				<div className="flex min-w-0 items-center gap-1">
					<DetailBreadcrumb
						parentLabel="Members"
						onParentClick={close}
						currentIcon={
							<PresenceAvatar
								userId={id}
								size="xs"
								name={name}
								image={user.imageUrl}
								dotSize="xs"
							/>
						}
						currentLabel={name}
					/>
					{isAdmin && !isSelf && (
						<DropdownMenu
							trigger={
								<IconButton
									Icon={MoreVertical}
									variant="ghost"
									iconClassName="fill-current"
								/>
							}
							contentProps={{ side: "bottom", align: "start" }}
						>
							<DropdownMenuGroup>
								<DropdownMenuItem onClick={() => setRoleDialogOpen(true)}>
									Change role
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => setConfirmRemove(true)}>
									Remove member
								</DropdownMenuItem>
							</DropdownMenuGroup>
						</DropdownMenu>
					)}
				</div>
				<div className="flex items-center gap-2">
					{isAdmin && !isSelf && (
						<Button
							variant="outline"
							size="sm"
							onClick={() => setRoleDialogOpen(true)}
						>
							Change role
						</Button>
					)}
				</div>
			</DetailTopBar>
			<DetailScreenLayout>
				<DetailScreenEditor isPadded={false}>
					<div className="flex items-center gap-3 px-5 pb-3 pt-6 sm:px-8">
						<PresenceAvatar
							userId={id}
							size="xl"
							name={name}
							image={user.imageUrl}
							roundness="full"
							dotSize="lg"
						/>
						<div className="flex min-w-0 flex-col gap-0.5">
							<div className="flex min-w-0 items-center gap-2">
								<span className="truncate font-medium">{name}</span>
								<Badge variant="dark-gray">{ROLE_LABEL[role]}</Badge>
								<OutOfOfficeBadge until={membership.outOfOfficeUntil} />
							</div>
							<div className="group/email flex min-w-0 items-center gap-1.5 text-sm text-secondary">
								<span className="truncate">{user.email}</span>
								<CopyButton
									value={user.email}
									className="flex-none opacity-0 transition-opacity duration-100 group-hover/email:opacity-100"
								/>
							</div>
						</div>
					</div>

					<div className="flex flex-col px-5 pb-2 text-xs sm:px-8">
						<OutOfOfficeFact membership={membership} />
						<MemberFactRow
							Icon={ClockIcon}
							label={formatPresenceLabel(isOnline, lastSeenAt)}
						/>
						{localTime && (
							<MemberFactRow
								Icon={GlobeIcon}
								label={`${localTime} local time`}
							/>
						)}
						<MemberFactRow
							Icon={JoinedIcon}
							label={`Joined ${new Date(membership.createdAt).toLocaleDateString()}`}
						/>
					</div>

					<div className="mb-4 mt-8 flex min-h-7 w-full items-center px-5 sm:px-8">
						<div className="text-sm font-medium">Assigned threads</div>
					</div>

					<DetailThreadsList
						assigneeId={id}
						hideLabel
						controls={threadControls}
						setThreadsSize={threadsState.report}
					/>
					{threadsState.isEmpty && (
						<div className="flex min-h-[40dvh] w-full items-center justify-center py-8">
							<Empty
								text={`Nothing is assigned to ${name} right now.`}
								title="Assigned threads"
								className="max-w-[20rem]"
								variant="left"
								image={<img src={EmptyIssue} alt="No assigned threads" />}
								action={
									<Button
										onClick={() => void navigate({ to: "/threads" })}
										className="min-w-32"
									>
										Go to Inbox
									</Button>
								}
							/>
						</div>
					)}
				</DetailScreenEditor>
			</DetailScreenLayout>
		</>
	)
}

const EmptyMemberDetailsScreen = () => {
	return (
		<div className="flex min-h-0 w-full flex-1 items-center justify-center">
			<Empty
				text="They may have left the workspace, or you may not have access."
				title="Member not found"
				className="max-w-[20rem]"
				variant="center"
				image={<img src={EmptyCompany} alt="Member not found" />}
			/>
		</div>
	)
}
